import {
  Component,
  DestroyRef,
  HostBinding,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractDeclarativeInputFieldComponent,
  DeclarativeFieldComponent,
  FormInputField,
} from '@fmnts/declarative-forms';
import { faCompress, faExpand } from '@fortawesome/pro-solid-svg-icons';
import { filter } from 'rxjs';
import { SharedDeclarativeCampaignFormsModule } from '../../shared-declarative-next-forms.module';
import {
  SignaturePadDirective,
  SignatureValue,
} from './signature-pad.directive';

@Component({
  selector: 'next-signature-field',
  templateUrl: './signature-field.component.html',
  standalone: true,
  imports: [SharedDeclarativeCampaignFormsModule, SignaturePadDirective],
})
export class SignatureFieldComponent extends AbstractDeclarativeInputFieldComponent<
  FormInputField,
  SignatureValue
> {
  private readonly fieldComponent = inject(DeclarativeFieldComponent);
  private readonly destroyRef = inject(DestroyRef);

  @HostBinding('class') componentClass = 'form-field form-field__signature';

  @ViewChild(SignaturePadDirective, { static: true })
  signaturePad!: SignaturePadDirective;

  protected showAsModal = false;

  /** @internal */
  protected readonly _iconShowFullscreen = faExpand;
  /** @internal */
  protected readonly _iconHideFullscreen = faCompress;

  private _value: SignatureValue = null;

  get value() {
    return this._value;
  }

  set value(value) {
    this._value = value;
    this._onChange(value);
    this._onTouched();
  }

  get showError(): boolean {
    return (
      this.form && (this.value || this.control.touched) && this.control.invalid
    );
  }

  constructor() {
    super();

    this.fieldComponent.stateChange$
      .pipe(
        filter(({ type }) => type === 'step-visible'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.signaturePad.refresh();
      });
  }

  writeValue(value) {
    this._value = value;

    setTimeout(() => this.signaturePad.fromData(this._value?.data), 0);
  }

  override setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);
    if (!isDisabled) {
      this.signaturePad.refresh();
    }
  }

  protected updateValue(value: SignatureValue) {
    this.value = value;
  }

  protected toggleModal() {
    this.showAsModal = !this.showAsModal;
  }

  protected clearValue() {
    this.value = null;
    this.signaturePad.clear();
  }
}
